import { useCallback, useMemo, useState } from 'react';

import { ApprovalStatus, BillingGroupRun, DateStr, GUID, RunStatus } from '@ndvr/billing-api';

export const isApprovableRun = (run: BillingGroupRun) =>
    run.runStatus === RunStatus.COMPLETED && run.approvalStatus === ApprovalStatus.UNAPPROVED;

export const runSelectionBlockReason = (run: BillingGroupRun, canRerun: boolean) => {
    if (canRerun) return undefined;
    if (run.approvalStatus === ApprovalStatus.APPROVED) return 'This billing run is already approved.';
    if (run.runStatus !== RunStatus.COMPLETED) return 'Only completed billing runs can be approved.';
    return undefined;
};

type RunSelection = {
    billingDate: DateStr;
    runGUIDs: Set<GUID<BillingGroupRun>>;
};

export type BillingRunSelection = {
    selectedRuns: BillingGroupRun[];
    selectedRunGUIDs: Set<GUID<BillingGroupRun>>;
    setRunsSelected: (runGUIDs: GUID<BillingGroupRun>[], selected: boolean) => void;
    replaceSelection: (runGUIDs: GUID<BillingGroupRun>[]) => void;
    clearSelection: () => void;
};

// The selection is tied to the billing date it was made on, so switching dates starts empty,
// and runs that disappear or become unselectable after a refetch drop out of it.
export const useBillingRunSelection = (
    billingDate: DateStr,
    runs: BillingGroupRun[],
    canSelectRun: (run: BillingGroupRun) => boolean,
): BillingRunSelection => {
    const [selection, setSelection] = useState<RunSelection>(() => ({ billingDate, runGUIDs: new Set() }));

    const selectedRuns = useMemo(() => {
        if (selection.billingDate !== billingDate) return [];
        return runs.filter((run) => selection.runGUIDs.has(run.billingGroupRunGUID) && canSelectRun(run));
    }, [billingDate, canSelectRun, runs, selection]);
    const selectedRunGUIDs = useMemo(() => new Set(selectedRuns.map((run) => run.billingGroupRunGUID)), [selectedRuns]);

    const setRunsSelected = useCallback(
        (runGUIDs: GUID<BillingGroupRun>[], selected: boolean) =>
            setSelection((previous) => {
                const nextRunGUIDs = new Set(previous.billingDate === billingDate ? previous.runGUIDs : []);
                runGUIDs.forEach((runGUID) => (selected ? nextRunGUIDs.add(runGUID) : nextRunGUIDs.delete(runGUID)));
                return { billingDate, runGUIDs: nextRunGUIDs };
            }),
        [billingDate],
    );
    const replaceSelection = useCallback(
        (runGUIDs: GUID<BillingGroupRun>[]) => setSelection({ billingDate, runGUIDs: new Set(runGUIDs) }),
        [billingDate],
    );
    const clearSelection = useCallback(() => replaceSelection([]), [replaceSelection]);

    return { selectedRuns, selectedRunGUIDs, setRunsSelected, replaceSelection, clearSelection };
};
